import { useLazyQuery, useQuery } from '@apollo/client';
import { UserDto } from '@ecdlink/core';
import { UserList, SortEnumType, getUserCount } from '@ecdlink/graphql';
import {
  DropDownOption,
  Dropdown,
  SearchDropDown,
  SearchDropDownOption,
  Typography,
} from '@ecdlink/ui';
import { useEffect, useMemo, useState } from 'react';
import { ContentLoader } from '../../../../components/content-loader/content-loader';
import UiTable from '../../../../components/ui-table';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
} from '@heroicons/react/solid';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router';
import { endOfDay, format, startOfDay } from 'date-fns';
import DatePicker from 'react-datepicker';
import { Status } from '../application-admins/applications-admins.types';
import { UserRoles } from '../../../../constants/user';
import { UsersRouteRedirectTypeEnum } from '../../../view-user/view-user.types';

export const sortByClientStatusOptions: SearchDropDownOption<string>[] = [
  Status?.ACTIVE,
  Status?.INACTIVE,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export default function ApplicationUsers() {
  const [searchValue, setSearchValue] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [nameFilter, setNameFilter] = useState(false);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>();
  const [sortDescending, setSortDescending] = useState<boolean>(true);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedPageSize] = useState<number>(20);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);
  const onChange = (dates) => {
    const [start, end] = dates;
    setPendingStartDate(start);
    setPendingEndDate(end);
  };

  const [filterDateAdded, setFilterDateAdded] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    SearchDropDownOption<string>[]
  >([sortByClientStatusOptions[0]]);

  const dateDropdownValue = useMemo(
    () =>
      startDate && endDate
        ? `${format(startDate, 'd MMM yy')} - ${format(endDate, 'd MMM yy')}`
        : '',
    [endDate, startDate]
  );

  // TODO: When sort by gets implemented, this could be useful, else remove.
  // type SortInput = { [Key in keyof ApplicationUserSortInput]?: ApplicationUserSortInput[Key] };
  // const getSortInput = (sorts: SortInput[]) => {
  //   return sorts.map(sort => sort);
  // };
  //
  // const sortinput = getSortInput([
  //   { insertedDate : sortDescending ? SortEnumType.Desc : SortEnumType.Asc },
  //   { fullName: sortDescending ? SortEnumType.Desc : SortEnumType.Asc }
  // ]);
  const history = useHistory();

  const componentString = (role?: string) => {
    switch (role) {
      case UserRoles.Administrator:
        return UsersRouteRedirectTypeEnum.admins;
      case UserRoles.TeamLead:
        return UsersRouteRedirectTypeEnum.teamLeads;
      case UserRoles.HealtCareWorker:
        return UsersRouteRedirectTypeEnum.chw;
      default:
        return UsersRouteRedirectTypeEnum.admins;
    }
  };

  const viewSelectedRow = (selectedRow: any) => {
    localStorage.setItem(
      'selectedUser',
      selectedRow?.userId ?? selectedRow?.id
    );
    history.push({
      pathname: '/users/view-user',
      state: {
        component: componentString(selectedRow?.roles?.[0]?.name),
        userId: selectedRow?.id,
        hcwId: selectedRow?.id,
      },
    });
  };

  const isActiveFilter = useMemo(() => {
    if (statusFilter?.length === 1) {
      return statusFilter[0]?.value === Status?.ACTIVE;
    }
    return null;
  }, [statusFilter]);

  const getDateFilterBy = (fromDate?: Date | null, toDate?: Date | null) =>
    fromDate && toDate
      ? [
          {
            fieldName: 'InsertedDate',
            filterType: 'GREATER_THAN_OR_EQUAL',
            value: startOfDay(fromDate).toISOString(),
          },
          {
            fieldName: 'InsertedDate',
            filterType: 'LESS_THAN_OR_EQUAL',
            value: endOfDay(toDate).toISOString(),
          },
        ]
      : undefined;

  const getVariables = (
    search: string,
    sortDescending: boolean,
    currentPage: number,
    pageSize: number,
    fromDate?: Date | null,
    toDate?: Date | null,
    isActive?: boolean | null
  ) => {
    const filterBy = getDateFilterBy(fromDate, toDate);

    return {
      search: search,
      isActive: isActive,
      order: [
        { insertedDate: sortDescending ? SortEnumType.Desc : SortEnumType.Asc },
        { fullName: sortDescending ? SortEnumType.Desc : SortEnumType.Asc },
      ],
      pagingInput: {
        pageNumber: currentPage,
        pageSize: pageSize,
        ...(filterBy ? { filterBy } : {}),
      },
    };
  };

  const [getAllUsers, { data, loading }] = useLazyQuery(UserList, {
    variables: getVariables(searchValue, sortDescending, selectedPage, 100),
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    getAllUsers({
      variables: getVariables(
        searchValue,
        sortDescending,
        selectedPage,
        selectedPageSize,
        startDate,
        endDate,
        isActiveFilter
      ),
    });
  }, [
    getAllUsers,
    searchValue,
    selectedPage,
    selectedPageSize,
    sortDescending,
    startDate,
    endDate,
    isActiveFilter,
  ]);

  const { data: countData } = useQuery(getUserCount, {
    variables: {
      search: searchValue,
      isActive: isActiveFilter,
      pagingInput: {
        pageNumber: 1,
        ...(getDateFilterBy(startDate, endDate)
          ? { filterBy: getDateFilterBy(startDate, endDate) }
          : {}),
      },
    },
    fetchPolicy: 'network-only',
  });
  const totalRows: number = countData?.countUsers ?? 0;

  const goToPage = (page: number) => setSelectedPage(Math.max(page, 1));

  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    if (data && data.users) {
      const copyItems = data.users;
      const modifiedData = copyItems.map((obj) => {
        const newUserData = {
          ...obj,
          displayColumnIdPassportEmail:
            obj?.userName || obj?.idNumber || obj?.email || '',
        };
        const { __typename: _, roles, ...rest } = newUserData;
        const modifiedRoles = roles.map((role) => {
          const { __typename: __, ...roleRest } = role;
          return roleRest;
        });
        return { ...rest, roles: modifiedRoles };
      });

      setTableData(modifiedData);
    }
  }, [data]);

  const search = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value || '');
        setSelectedPage(1);
      }, 400),
    []
  );

  const clearFilters = () => {
    setNameFilter(false);
    setSelectedRoleFilter('');
    setEndDate(null);
    setStartDate(null);
    setPendingStartDate(null);
    setPendingEndDate(null);
    setFilterDateAdded(false);
    setStatusFilter([]);
    setSelectedPage(1);
  };

  const openDatePicker = () => {
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setFilterDateAdded(true);
  };

  const applyDateFilter = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setFilterDateAdded(false);
    setSelectedPage(1);
  };

  const cancelDateFilter = () => {
    setFilterDateAdded(false);
  };

  const hasDateFilter = useMemo(() => (!startDate ? 0 : 1), [startDate]);
  const numberOfFilters = useMemo(
    () => statusFilter?.length + hasDateFilter,
    [statusFilter?.length, hasDateFilter]
  );

  const renderFilterButtonText = useMemo(() => {
    if (numberOfFilters) {
      if (numberOfFilters === 1) {
        return `${numberOfFilters} Filter`;
      }
      return `${numberOfFilters} Filters`;
    }

    return 'Filter';
  }, [numberOfFilters]);

  if (tableData) {
    return (
      <div>
        <div className="flex flex-col">
          <div className="pb-5 sm:flex sm:items-center sm:justify-between">
            <div className="text-body w-full sm:flex  sm:justify-around">
              <div className="text-body w-full flex-col sm:flex sm:justify-around">
                <div className="relative w-auto">
                  <span className="absolute inset-y-1/2 left-3 mr-4 flex -translate-y-1/2 transform items-center">
                    {searchValue === '' && (
                      <SearchIcon className="h-5 w-5 text-black"></SearchIcon>
                    )}
                  </span>
                  <input
                    className="focus:outline-none sm:text-md block w-full rounded-md bg-white py-3 pl-10 pr-3 leading-5 text-gray-900 placeholder-gray-600 focus:border-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-white"
                    placeholder="      Search by name, ID or cellphone..."
                    onChange={search}
                  />
                </div>
                {showFilter && (
                  <div className="my-4 flex w-full flex-row items-center ">
                    <div className="min-w relative mr-2 flex items-center gap-2">
                      <div onClick={openDatePicker}>
                        <Dropdown
                          fillType="filled"
                          textColor={'textLight'}
                          fillColor={endDate ? 'secondary' : 'white'}
                          placeholder={dateDropdownValue || 'Date invited'}
                          labelColor={endDate ? 'white' : 'textLight'}
                          list={[]}
                          onChange={(item) => {}}
                          className="w-56 text-sm text-white"
                        />
                      </div>
                      {filterDateAdded && (
                        <div className="border-uiLight absolute left-0 top-full z-20 mt-2 rounded-md border bg-white p-3 shadow-lg">
                          <DatePicker
                            selected={pendingStartDate}
                            onChange={onChange}
                            startDate={pendingStartDate}
                            endDate={pendingEndDate}
                            selectsRange={true}
                            inline
                            monthsShown={2}
                            calendarClassName="application-users-date-range-picker"
                          />
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              onClick={cancelDateFilter}
                              type="button"
                              className="text-textMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={applyDateFilter}
                              disabled={!pendingStartDate || !pendingEndDate}
                              type="button"
                              className="bg-secondary hover:bg-uiMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="relative mr-2 flex items-center gap-2">
                      <SearchDropDown<string>
                        className={'w-full'}
                        menuItemClassName={
                          'absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto border border-gray-200 shadow-lg'
                        }
                        options={sortByClientStatusOptions}
                        selectedOptions={statusFilter}
                        onChange={(value) => {
                          setStatusFilter(value);
                          setSelectedPage(1);
                        }}
                        placeholder={'Status'}
                        multiple={true}
                        color={'secondary'}
                        info={{
                          name: `Status:`,
                        }}
                      />
                    </div>

                    <div className="flex w-3/12 justify-end">
                      <div className="">
                        <button
                          onClick={clearFilters}
                          type="button"
                          className="text-secondary hover:bg-secondary outline-none inline-flex w-full items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium hover:text-white "
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mx-2 w-3/12">
                <span className="w-full text-lg font-medium leading-6 text-gray-900">
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    id="dropdownHoverButton"
                    className={`${
                      numberOfFilters
                        ? ' bg-secondary'
                        : 'border-secondary border-2 bg-white'
                    } focus:border-secondary focus:outline-none focus:ring-secondary dark:bg-secondary dark:hover:bg-grey-300 dark:focus:ring-secondary inline-flex items-center rounded-lg px-4 py-2.5 text-center text-sm font-medium ${
                      numberOfFilters ? 'text-white' : 'text-textMid'
                    } hover:bg-gray-300 focus:ring-2`}
                    type="button"
                  >
                    <div className="flex items-center gap-1">
                      <Typography
                        className="truncate"
                        type="help"
                        color={numberOfFilters ? 'white' : 'textLight'}
                        text={renderFilterButtonText}
                      />
                      {!showFilter ? (
                        <span>
                          <ChevronDownIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      ) : (
                        <span>
                          <ChevronUpIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      )}
                    </div>
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                <UiTable
                  columns={[
                    {
                      field: 'displayColumnIdPassportEmail',
                      use: 'ID/Passport/Email',
                    },
                    { field: 'fullName', use: 'Name' },
                    {
                      field: 'roles',
                      use: 'Role',
                      type: 'array',
                      displayProperty: 'name',
                    },
                    { field: 'insertedDate', use: 'date Invited' },
                    { field: 'isActive', use: 'Status' },
                  ]}
                  rows={tableData}
                  component={'administrators'}
                  viewRow={viewSelectedRow}
                  noBulkSelection
                  isLoading={loading}
                  totalRows={totalRows}
                  currentPage={selectedPage}
                  pageSize={selectedPageSize}
                  onPageChange={goToPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <ContentLoader />;
  }
}
